import {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import { AlertCircle } from 'lucide-react';
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import ErrorBoundary from '../../../../components/shared/error/ErrorBoundary';
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nhlDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nhl-dash.pt-br";
import { useRTDBGames } from '../rtdb/hooks';
import { useQuickPrediction } from '../../../../components/dashboards/hooks/useQuickPrediction';
import { getCurrentNHLSeason, formatNHLSeason } from '../utils/nhlSeasonDetector';
import MobileNHLPredictionSheet from '../components/mobile/MobileNHLPredictionSheet';
import { isGameLive, isGameFinal } from '../rtdb/rtdbConfig';
import { Header } from '../../../../components/dashboards/components/shared/Header';
import { NHL_HEADER_COPY } from '../constants/headerContent';
import MobileNHLGamesView from '../components/mobile/MobileNHLGamesView';
import NHLOddsModal from "../NHLOddsModal";
import RailNavigation from '../../../../components/dashboards/mobile/RailNavigation';
import { MarketSheet } from '../../../../components/dashboards/mobile/FilterSheets';
import useNHLRailFilters from '../../../../components/dashboards/mobile/hooks/useNHLRailFilters';
import { DatePickerSheet } from '../../../../components/dashboards/components/shared/date-picker';
import { ensureISODate } from '../../../../components/dashboards/utils/dateUtils';

const DEFAULT_GAMES_PAGINATION = {
  currentPage: 1,
  perPage: 6,
  totalPages: 1
};

const NHLMobileDashboard = () => {

  // State management
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [gamesPagination, setGamesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [isPredictionSheetOpen, setIsPredictionSheetOpen] = useState(false);
  const [selectedGameForPrediction, setSelectedGameForPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  // Get current season info
  const seasonInfo = getCurrentNHLSeason();
  const seasonDisplay = formatNHLSeason(seasonInfo);

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    if (nextFilters.date) {
      setSelectedDate(ensureISODate(nextFilters.date, selectedDate));
    }
  }, [selectedDate]);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showStatusSheet,
    setShowStatusSheet
  } = useNHLRailFilters({
    initialDate: selectedDate,
    onFiltersChange: handleRailFiltersChange
  });

  // Fetch games using RTDB hook
  const {
    games = [],
    loading: gamesLoading,
    error: gamesError,
    refetch: refetchGames
  } = useRTDBGames({
    date: selectedDate,
    season: seasonInfo.season,
    teamId: null,
    enabled: true,
    realtime: true
  });

  // Prediction hook
  const {
    isLoading: isPredicting,
    selectedGames: selectedGamesForPrediction,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    isGameFrozen,
    selectionCount,
    checkCachedPredictions
  } = useQuickPrediction();

  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? nhlDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const handleViewOdds = useCallback((game) => {
    setSelectedGameForOdds(game);
  }, []);

  const handleCloseOdds = useCallback(() => setSelectedGameForOdds(null), []);

  // Calculate new selections (excluding frozen games with predictions)
  const newSelectionCount = useMemo(() => {
    return selectedGamesForPrediction.filter(game => !isGameFrozen(game.id)).length;
  }, [selectedGamesForPrediction, isGameFrozen]);

  const handleViewPrediction = useCallback((game) => {
    setSelectedGameForPrediction(game);
    setIsPredictionSheetOpen(true);
  }, []);

  const handleClosePredictionSheet = useCallback(() => {
    setIsPredictionSheetOpen(false);
    setSelectedGameForPrediction(null);
  }, []);

  const handlePredictWinners = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames('nhl');
    setPredictionFlowActive(false);
  }, [predictSelectedGames]);

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  // Prime predictions from cache/Firestore when games update
  useEffect(() => {
    if (!games?.length) return;
    const gameIds = games.map((g) => g.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("nhl", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NHL mobile):", err)
    );
  }, [games, checkCachedPredictions]);

  // Filter games based on rail filters
  const filteredGames = useMemo(() => {
    let filtered = [...games];

    // Filter by status
    if (railFilters.status === 'live') {
      filtered = filtered.filter((game) => isGameLive(game?.status?.short));
    } else if (railFilters.status === 'upcoming') {
      filtered = filtered.filter(
        (game) => !isGameLive(game?.status?.short) && !isGameFinal(game?.status?.short)
      );
    } else if (railFilters.status === 'finished') {
      filtered = filtered.filter((game) => isGameFinal(game?.status?.short));
    }

    // Filter by conference/division (if teams have this data)
    // Note: This might need adjustment based on actual NHL team data structure
    if (railFilters.conference !== 'all' && railFilters.division === 'all') {
      // Conference filter only
      filtered = filtered.filter((game) => {
        const homeConf = game?.teams?.home?.conference?.toLowerCase();
        const awayConf = game?.teams?.away?.conference?.toLowerCase();
        return homeConf === railFilters.conference || awayConf === railFilters.conference;
      });
    } else if (railFilters.division !== 'all') {
      // Division filter
      filtered = filtered.filter((game) => {
        const homeDiv = game?.teams?.home?.division?.toLowerCase();
        const awayDiv = game?.teams?.away?.division?.toLowerCase();
        return homeDiv === railFilters.division || awayDiv === railFilters.division;
      });
    }

    return filtered;
  }, [games, railFilters]);

  useEffect(() => {
    setGamesPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil(filteredGames.length / prev.perPage));
      return {
        ...prev,
        totalPages,
        currentPage: Math.min(prev.currentPage, totalPages)
      };
    });
  }, [filteredGames]);

  useEffect(() => {
    if (isPredicting) {
      return;
    }

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  const liveGames = games.filter((game) => isGameLive(game?.status?.short));
  const finishedGames = games.filter((game) => isGameFinal(game?.status?.short));
  const upcomingGames = games.filter(
    (game) => !isGameLive(game?.status?.short) && !isGameFinal(game?.status?.short)
  );

  return (
    <ErrorBoundary onReset={refetchGames}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader title="NHL Dashboard" showBackButton />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...NHL_HEADER_COPY} />

          <div className="mt-4 mb-4">
            <RailNavigation
              sport="nhl"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          {gamesError && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="text-red-300" size={18} />
              <p>{gamesError}</p>
            </div>
          )}

          <MobileNHLGamesView
            games={filteredGames}
            loading={gamesLoading}
            pagination={gamesPagination}
            onPageChange={handleGamesPageChange}
            t={t}
            isGameSelected={isGameSelected}
            isGameFrozen={isGameFrozen}
            onToggleSelection={toggleGameSelection}
            onViewPrediction={handleViewPrediction}
            getGamePrediction={getGamePrediction}
            onViewOdds={handleViewOdds}
        />
        </div>

        {/* Filter Sheets */}
        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date || selectedDate)}
          onDateSelect={(isoDate) => {
            const formatted = ensureISODate(isoDate, selectedDate);
            updateRailFilter("date", formatted);
            setSelectedDate(formatted);
            setShowDateSheet(false);
          }}
        />

        <MarketSheet
          isOpen={showStatusSheet}
          onClose={() => setShowStatusSheet(false)}
          currentMarket={railFilters.status || 'all'}
          onMarketChange={(status) => {
            updateRailFilter('status', status);
            setShowStatusSheet(false);
          }}
          sport="nhl"
        />

        <MobileNHLPredictionSheet
          isOpen={isPredictionSheetOpen}
          game={selectedGameForPrediction}
          prediction={selectedGameForPrediction ? getGamePrediction(selectedGameForPrediction.id) : null}
          selectionCount={newSelectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          onPredict={handlePredictWinners}
          onClear={clearSelections}
          onClose={handleClosePredictionSheet}
          t={t}
        />

        {selectedGameForOdds && (
          <NHLOddsModal
            game={selectedGameForOdds}
            isOpen={!!selectedGameForOdds}
            onClose={handleCloseOdds}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default NHLMobileDashboard;
